<template>
  <b-modal
    id="modal-coordinator-sop"
    v-model="showModal"
    :title="formData?.name || '-'"
    size="lg"
    centered
    @hidden="onHidden"
  >
    <div v-if="formData && localDetails.length">
      <!-- ACCORDION CHECKLIST DETAIL -->
      <div class="checklist-accordion">
        <b-card
          v-for="detail in filteredDetails"
          :key="detail.checklist_detail_id"
          no-body
          class="mb-3 shadow-sm"
          :class="{ 'disabled-section': !canAccessSection(detail) }"
        >
          <!-- ACCORDION HEADER -->
          <b-card-header class="p-0">
            <b-button
              v-b-toggle="'accordion-' + detail.checklist_detail_id"
              :disabled="!canAccessSection(detail)"
              variant="light"
              class="accordion-header-btn d-flex justify-content-between align-items-center w-100 text-left"
              :class="{
                'opacity-50': !canAccessSection(detail),
                completed: detail.check === true,
              }"
            >
              <div class="d-flex align-items-center flex-grow-1">
                <span class="font-weight-bold mr-2">
                  {{ detail.description }}
                  <span v-if="detail.is_mandatory" class="text-danger">*</span>
                </span>
                <span v-if="!canAccessSection(detail)" class="badge badge-warning">
                  {{ getBlockedReason(detail) }}
                </span>
                <span v-else-if="detail.check === true" class="badge badge-success">
                  ✓ Selesai
                </span>
              </div>
              <b-form-checkbox
                class="checkbox-lg ml-3"
                v-model="detail.check"
                disabled
                @click.stop
              />
            </b-button>
          </b-card-header>

          <!-- ACCORDION CONTENT -->
          <b-collapse
            :id="'accordion-' + detail.checklist_detail_id"
            class="accordion-content"
            :visible="isAccordionOpen(detail)"
          >
            <b-card-body class="pt-4">
              <!-- TYPE: U (UPLOAD FOTO) -->
              <div v-if="detail.checklist_detail_type.includes('U')">
                <div class="upload-wrapper">
                  <div
                    class="upload-box"
                    @click="canUpload && triggerFile(detail.checklist_detail_id)"
                    @dragover.prevent="canUpload && (isDragging = true)"
                    @drop.prevent="canUpload && onDrop($event, detail)"
                    :class="{ dragging: isDragging, disabled: !canUpload }"
                    @dragleave="isDragging = false"
                  >
                    <!-- Preview -->
                    <img
                      v-if="preview[detail.checklist_detail_id]"
                      :src="preview[detail.checklist_detail_id]"
                      class="uploaded-image"
                    />

                    <!-- Placeholder -->
                    <div v-else class="upload-placeholder">
                      <i class="mc-fa icon-outline_camera_alt" style="font-size: 32px"></i>
                      <p>Tap atau Drop gambar</p>
                    </div>
                  </div>

                  <!-- Hidden Input -->
                  <input
                    :id="'upl-' + detail.checklist_detail_id"
                    type="file"
                    accept="image/*"
                    class="hidden-file"
                    @change="onFileSelected($event, detail)"
                  />
                </div>
              </div>

              <!-- TYPE: RCD (Register Create Device) -->
              <div v-else-if="detail.checklist_detail_type.includes('RCD')">
                <FormCreateDevice
                  :disabled="isReadOnly"
                  :form="form"
                  :deviceTypeOptions="deviceTypes"
                  :isEdit="isEditRegister"
                  :accordionVisible.sync="accordionVisible"
                  :responseModal="responseModal"
                  :hasAccessAll="hasAccessAll('update-imei:update')"
                  :disabledRCD="disabledRCD"
                  @submit-imei="processSubmit"
                  @device-auto-selected="(device) => (form.deviceTypes = device)"
                  @update-device="updateDevice"
                />
                <b-row class="mc-modal__filter-footer mt-4">
                  <b-col sm="12" md="6" class="mc-modal__filter-footer-fav">
                    <div class="button-items"></div>
                  </b-col>
                  <b-col sm="12" md="6" class="mc-modal__filter-footer-btn">
                    <b-button
                      variant="outline-primary"
                      @click="nextSetup(form)"
                      class="me-3"
                      :disabled="!form.imei"
                    >
                      <i class="mc-fa icon-fill_keypad"></i>
                      Konfigurasi lebih lanjut
                    </b-button>
                  </b-col>
                </b-row>
              </div>

              <!-- TYPE: SD (Set Device to Customer) -->
              <div v-else-if="detail.checklist_detail_type.includes('SD')">
                <FormDeviceSetToCustomer
                  v-if="!isLoading"
                  :disabled="isReadOnly"
                  ref="deviceSetupForm"
                  :isEdit="isEditSetTo"
                  :disabledSD="disabledSD"
                  v-model="set"
                  :taskData="taskData"
                />
                <b-row class="mc-modal__filter-footer mt-4">
                  <b-col sm="12" md="8" class="mc-modal__filter-footer-fav"> </b-col>
                  <b-col sm="12" md="4" class="mc-modal__filter-footer-btn">
                    <b-button
                      v-if="!isEditSetTo"
                      :disabled="setDeviceToCustomerLoading || disabledSD"
                      type="submit"
                      variant="primary"
                      @click="registerToCompany"
                    >
                      <b-spinner v-if="setDeviceToCustomerLoading" small />
                      Simpan
                    </b-button>
                    <b-button
                      v-else
                      :disabled="isLoading || setDeviceToCustomerLoading || disabledSD"
                      type="submit"
                      variant="primary"
                      @click="onUpdateVehicle"
                    >
                      <b-spinner v-if="setDeviceToCustomerLoading" small />
                      Ubah
                    </b-button>
                  </b-col>
                </b-row>
              </div>

              <!-- TYPE: CUCWO (Check & Update Check Work Order) -->
              <div v-else-if="detail.checklist_detail_type.includes('CUCWO')">
                <McButton
                  variant="primary"
                  :text="'Check & Update Work Order'"
                  @click="onCheckAndUpdateWorkOrderErp"
                  class="d-inline-flex"
                  :disabled="isLoading || isLoadingCheckUpdate || isCUCWOCompleted || isReadOnly"
                >
                  <b-spinner v-if="isLoadingCheckUpdate" small />
                </McButton>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </div>

    <!-- FOOTER -->
    <template #modal-footer>
      <template v-if="!isReadOnly">
        <b-button variant="outline-primary" :disabled="isLoading" @click="saveWithStatus('D')">
          <b-spinner v-if="isLoading" small class="mr-2" />
          {{ isLoading ? (isSaving ? $t('global.saving') : $t('global.loading')) : 'Draft (D)' }}
        </b-button>

        <b-button
          variant="primary"
          :disabled="!isAllValid || isLoading"
          @click="saveWithStatus('C')"
        >
          <b-spinner v-if="isLoading" small class="mr-2" />
          {{ isLoading ? (isSaving ? $t('global.saving') : $t('global.loading')) : 'Created (C)' }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import * as checklistApi from '@/services/rest-api-sms/lib/checklist/checklist'
import * as taskChecklistApi from '@/services/rest-api-sms/lib/checklist/tasks-checklist'
import FormCreateDevice from './FormCreateDevice'
import { required } from 'vuelidate/lib/validators'
import { isIndonesianPhoneNumber } from '@/helpers/validators'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import FormDeviceSetToCustomer from './FormDeviceSetToCustomer'
import McButton from '@/components/McButton'
import * as deviceApi from '@/services/rest-api-sms/lib/device/device'
import * as fileApi from '@/services/rest-api-sms/lib/file/file'
import * as vehicleApi from '@/services/rest-api-sms/lib/vehicle/vehicle'
import * as companyApi from '@/services/rest-api-sms/lib/company/company'
import * as tasksApi from '@/services/rest-api-sms/lib/tasks/tasks'
import * as vehicleTypeApi from '@/services/rest-api-sms/lib/vehicle/vehicle-type'
import { TASK_STATUS } from '@/constants/task-status'
import * as deviceTypeApi from '@/services/rest-api-sms/lib/device-type/device-type'

export default {
  name: 'ModalCoordinatorSop',

  components: {
    FormCreateDevice,
    FormDeviceSetToCustomer,
    McButton,
  },

  mixins: [Helper],

  props: {
    value: Boolean,
    workOrderId: Number,
    mode: { type: String, default: 'add' },
    imei: String,
    taskData: Object,
    taskId: Number,
  },

  data() {
    return {
      showModal: false,
      isLoading: false,
      formData: null,
      localDetails: [],
      isDragging: false, // <--- penting
      form: {
        deviceTypes: null,
        companyId: 1,
        imei: null,
        phoneNo: null,
        vehicleId: null,
      },
      deviceTypes: [],
      isEditRegister: false,
      isEditSetTo: false,
      showDeviceType: false,
      accordionVisible: false,
      responseModal: {
        title: 'Registrasi IMEI Berhasil',
        imei: null,
        vehicleId: null,
        status: null,
      },
      // ⬇️ WAJIB ADA DARI AWAL
      set: {
        deviceName: null,
        vendorType: null,
        imei: null,
        phoneNo: null,
        vehicleId: null,
        companies: null,
        selectedVehicle: null,
        selectedVehicleType: null,
        odometer: null,
        hullNo: null,
        vehicleYear: null,
      },
      isLoadingCheckUpdate: false,
      setDeviceToCustomerLoading: false,
      isDeviceAlreadyRegistered: false, // 🔥 sumber kebenaran
      isSaving: false,
      isIMEIRegistered: false, // ★ NEW: Track apakah IMEI sudah ada di DB
      // ✅ NEW: Track mana accordion yang open
      accordionState: {}, // { detail_id: true/false }
      device: null,
      hasShownManualSetToToast: false,
    }
  },

  validations: {
    form: {
      deviceTypes: { required },
      imei: { required },
      phoneNo: { required, isIndonesianPhoneNumber },
    },
    set: {
      companies: { required },
      selectedVehicle: { required },
      selectedVehicleType: { required },
      phoneNo: { required, isIndonesianPhoneNumber },
      odometer: { required },
    },
  },

  computed: {
    // Filter hanya detail yang aktif
    filteredDetails() {
      return this.localDetails.filter((d) => d.is_active)
    },
    // Semua task sudah complete
    isAllValid() {
      return this.filteredDetails.every((d) => d.check === true)
    },
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    isReadOnly() {
      return this.taskData?.tasks_status !== 'P' || this.isManualSetTo
    },
    isManualSetTo() {
      return (
        !!this.device &&
        this.taskData?.vehicle_id !== this.device.vehicle_id &&
        this.device.company_id !== Number(process.env.VUE_APP_COMPANY_ID_MCEASY)
      )
    },
    rcdDetail() {
      return this.localDetails.find((d) => d.checklist_detail_type.includes('RCD'))
    },
    isRCDCompleted() {
      return this.rcdDetail?.check === true
    },
    disabledRCD() {
      return this.isReadOnly || this.isRCDCompleted
    },
    sdDetail() {
      return this.localDetails.find((d) => d.checklist_detail_type.includes('SD'))
    },
    cucwoDetail() {
      return this.localDetails.find((d) => d.checklist_detail_type.includes('CUCWO'))
    },
    isCUCWOCompleted() {
      return this.cucwoDetail?.check === true
    },
    canUpload() {
      // Upload allowed kalau modal not read-only
      return !this.isReadOnly
    },
    canChangeAfterCheck() {
      // Allow change/replace bahkan setelah checked
      return true // Selalu allow, atau set logic tertentu
    },
    disabledSD() {
      // ❌ SD DISABLED kalau:
      // 1. Modal read-only
      // 2. SD sudah selesai
      // 3. RCD BELUM selesai
      // 4. IMEI BELUM di-register di DB ★ NEW
      return (
        this.isReadOnly ||
        this.sdDetail?.check === true ||
        !this.isRCDCompleted ||
        !this.isIMEIRegistered
      ) // ★ NEW: Tunggu IMEI registered
    },
    isSDCompleted() {
      return this.sdDetail?.check === true
    },
    // ✅ Cek apakah RCD dan SD sudah selesai
    isRcdAndSdCompleted() {
      return this.isRCDCompleted && this.isSDCompleted
    },
  },

  watch: {
    value(val) {
      this.showModal = val
      if (val) this.initialize()
    },
    // ✅ NEW: Auto-expand SD saat RCD selesai
    isRCDCompleted(val) {
      if (val && this.sdDetail) {
        this.$set(this.accordionState, this.sdDetail.checklist_detail_id, true)
      }
    },
    // ✅ NEW: Auto-expand CUCWO saat SD selesai
    isSDCompleted(val) {
      if (val && this.cucwoDetail) {
        this.$set(this.accordionState, this.cucwoDetail.checklist_detail_id, true)
      }
    },
  },

  created() {
    this.preview = {}
    this.getDeviceTypesWithParams()
  },

  methods: {
    // ✅ NEW: Check apakah section bisa di-akses/di-expand
    canAccessSection(detail) {
      // RCD selalu bisa diakses
      if (detail.checklist_detail_type.includes('RCD')) {
        return !this.isReadOnly
      }
      // SD bisa diakses hanya jika RCD selesai
      if (detail.checklist_detail_type.includes('SD')) {
        return !this.isReadOnly && this.isRCDCompleted
      }
      // CUCWO bisa diakses hanya jika RCD dan SD selesai
      if (detail.checklist_detail_type.includes('CUCWO')) {
        return !this.isReadOnly && this.isRcdAndSdCompleted
      }
      // Upload tipe biasanya selalu bisa
      return !this.isReadOnly
    },

    // ✅ NEW: Get reason kenapa section di-block
    getBlockedReason(detail) {
      if (detail.checklist_detail_type.includes('SD')) {
        if (!this.isRCDCompleted) {
          return 'Selesaikan RCD dulu'
        }
      }
      if (detail.checklist_detail_type.includes('CUCWO')) {
        if (!this.isRCDCompleted) {
          return 'Selesaikan RCD & SD'
        }
        if (!this.isSDCompleted) {
          return 'Selesaikan SD dulu'
        }
      }
      return 'Belum bisa diakses'
    },

    // ✅ NEW: Check apakah accordion harus terbuka
    isAccordionOpen(detail) {
      // Default state: RCD terbuka, yang lain tertutup sampai bisa diakses
      const detailId = detail.checklist_detail_id

      // Jika state sudah di-track, gunakan itu
      if (this.accordionState.hasOwnProperty(detailId)) {
        return this.accordionState[detailId]
      }

      // Default: RCD always open, sisanya collapsed
      if (detail.checklist_detail_type.includes('RCD')) {
        return true
      }

      // Jika bisa diakses dan belum selesai, auto-expand
      if (this.canAccessSection(detail) && detail.check !== true) {
        return true
      }

      // Jika sudah selesai, tetap tertutup (atau bisa di-collapse)
      if (detail.check === true) {
        return false
      }

      return false
    },

    async updateChecklistItem(detail) {
      const payload = {
        tasks_id: this.taskId,
        tasks_status: TASK_STATUS.PLAN,
        tasks_checklist: [
          {
            tc_id: detail.tc_id, // null jika baru
            checklist_id: this.formData.checklist_id,
            tasks_checklist_detail: [
              {
                tcd_id: detail.tcd_id, // null jika baru
                checklist_detail_id: detail.checklist_detail_id,
                check: detail.check,
                photo: detail.photo_url,
                // ... field lain (note, dsb)
              },
            ],
          },
        ],
      }

      try {
        if (detail.tc_id) {
          await taskChecklistApi.update(this.taskId, payload) // PATCH
        } else {
          await taskChecklistApi.store(payload) // POST
          // setelah sukses, server akan mengembalikan data baru, bisa update tc_id & tcd_id
        }
      } catch (e) {
        // rollback lokal
        detail.check = !detail.check
        this.$mcToast.error('Gagal menyimpan perubahan checklist')
      }
    },

    // 4. UPDATE checkDeviceAlreadySetToCustomer() - Check isIMEIRegistered sebelum proses
    async checkDeviceAlreadySetToCustomer() {
      // 🔴 PRE-VALIDATION: Cek mandatory fields dulu SEBELUM API call
      if (!this.taskData.imei) {
        console.warn('Missing IMEI in taskData')
        return false
      }

      // ★ NEW: Cek apakah IMEI sudah registered
      if (!this.isIMEIRegistered) {
        console.warn('IMEI not registered yet')
        this.$mcToast.warning(
          'IMEI belum terdaftar. Selesaikan pendaftaran IMEI (RCD) terlebih dahulu.'
        )
        return false
      }

      // ❌ company_id = null atau 0 = INVALID, jangan call API + show toast
      if (!this.taskData.company_id || this.taskData.company_id === 0) {
        console.error('Company ID missing or invalid:', this.taskData.company_id)
        this.$mcToast.error(
          'Company belum diisi oleh teknisi. Hubungi supervisor untuk mengatur company.'
        )
        return false
      }

      // ❌ vehicle_type_id harus ada di taskData
      if (!this.taskData.vehicle_type_id || this.taskData.vehicle_type_id === 0) {
        console.error('Vehicle type ID missing:', this.taskData.vehicle_type_id)
        return false
      }

      try {
        // ✅ STEP 1: Verify IMEI ada di database (sudah di-check di checkDeviceAlreadyRegistered)
        // Tapi untuk consistency, verify sekali lagi
        const imeiResponse = await deviceApi.getByImei(this.taskData.imei)

        if (!imeiResponse?.status || !imeiResponse.data) {
          console.warn('IMEI not found in database (verify failed)')
          this.$mcToast.warning('IMEI tidak ditemukan. Periksa kembali registrasi IMEI.')
          this.isIMEIRegistered = false // ★ Update flag
          return false
        }

        // ★ Update flag - IMEI confirmed registered
        this.isIMEIRegistered = true

        // ✅ STEP 2: Verify data consistency antara taskData dan API
        const apiData = imeiResponse.data

        if (
          this.taskData.company_id !== apiData.company_id &&
          apiData.company_id !== Number(process.env.VUE_APP_COMPANY_ID_MCEASY)
        ) {
          console.warn('Company ID mismatch:', {
            taskData: this.taskData.company_id,
            api: apiData.company_id,
          })
          this.$mcToast.warning('Company ID tidak sesuai dengan data IMEI terdaftar.')
          return false
        }

        // ❌ vehicle_id = 0 atau null = INVALID, jangan call API
        let vehicleId = this.taskData.vehicle_id
        const odometer = this.taskData.odometer
        const hullNo = this.taskData.hull_no
        const licensePlate = this.taskData.license_plate
        const vehicleTypeId = this.taskData.vehicle_type_id
        const vehicleYear = this.taskData.vehicle_year

        if (vehicleId && vehicleId !== 0) {
          // ✅ STEP 3: Fetch vehicle & company detail HANYA untuk load data ke form
          const responseVehicle = await vehicleApi.detail(vehicleId)
          if (!responseVehicle?.status || !responseVehicle.data) {
            console.warn('Vehicle API failed or no data')
            this.$mcToast.error('Gagal mengambil data kendaraan. Silakan coba lagi.')
            return false
          }
        } else {
          vehicleId = apiData.vehicle_id
        }
        const companyResp = await companyApi.getById(
          this.taskData.company_id,
          this.taskData?.server_region || 'SG'
        )

        let companyData = null
        if (companyResp?.status && companyResp.data) {
          companyData = companyResp.data
        } else {
          // fallback
          // companyData = {
          //   company_id: this.taskData.company_id,
          //   name: companyData.name || 'Unknown',
          //   domain_name: companyData.domain_name || '',
          // }
          console.warn('Company API failed or no data')
          this.$mcToast.error('Gagal mengambil data perusahaan/customer. Silakan coba lagi.')
          return false
        }

        // ✅ Fetch vehicle type
        const responseVehicleType = await vehicleTypeApi.show(this.taskData.vehicle_type_id)

        let vehicleTypeName = 'Unknown - Unknown Unknown'
        if (responseVehicleType) {
          const mf = responseVehicleType.manufacturer || 'Unknown'
          const t = responseVehicleType.type || 'Unknown'
          vehicleTypeName = `${mf} ${t}`
        }

        // ✅ STEP 4: Set form dari taskData (BUKAN dari API)
        this.set = {
          ...this.set,
          imei: this.taskData.imei,
          deviceName: apiData.device_name || null,
          vendorType: apiData.vendor_type || companyData.name || null,
          phoneNo: this.taskData.phone_no || null,
          companies: {
            company_id: companyData.company_id,
            name: companyData.name,
            domain_name: companyData.domain_name || '',
          },
          odometer: odometer || 0,
          hullNo: hullNo || null,
          selectedVehicle: {
            vehicle_id: vehicleId,
            license_plate: licensePlate,
          },
          vehicleYear: vehicleYear || new Date().getFullYear(),
          vehicleId: vehicleId,
          selectedVehicleType: {
            vehicle_type_id: vehicleTypeId,
            vehicle_type_name: vehicleTypeName,
          },
        }
        if (apiData?.company_id !== Number(process.env.VUE_APP_COMPANY_ID_MCEASY)) {
          return true
        }
        return false
      } catch (error) {
        console.error('❌ checkDeviceAlreadySetToCustomer error:', error)
        this.$mcToast.error('Error mengambil data: ' + (error.message || 'Silakan coba lagi'))
        return false
      }
    },

    // 3. UPDATE checkDeviceAlreadyRegistered() - Set flag saat API call
    async checkDeviceAlreadyRegistered() {
      // 🔴 PRE-VALIDATION
      if (!this.taskData.imei) {
        console.warn('No IMEI in taskData')
        this.isIMEIRegistered = false // ★ Set flag false
        return false
      }

      try {
        // ❌ device_code harus ada sebelum cari di cache
        if (!this.taskData.device_code || this.taskData.device_code === 0) {
          console.warn('Invalid device_code:', this.taskData.device_code)
          this.isIMEIRegistered = false // ★ Set flag false
          return false
        }

        // ✅ STEP 1: Cari di cache deviceTypes
        let deviceType = this.deviceTypes.find((dt) => dt.deviceCode == this.taskData.device_code)

        if (deviceType) {
          // ✅ Device ditemukan di cache, langsung set
          this.form = {
            ...this.form,
            imei: this.taskData.imei,
            phoneNo: this.taskData.phone_no || null,
            companyId: this.taskData.company_id || null,
            deviceTypes: deviceType,
            vehicleId: null,
          }
          // ★ Set flag TRUE karena sudah di cache
          this.isIMEIRegistered = false
        }

        // ❌ Kalau tidak di cache, cek API UNTUK VERIFY
        try {
          const response = await deviceApi.getByImei(this.taskData.imei)

          if (response?.status && response.data) {
            this.device = response.data
            // ✅ IMEI ada di DB
            deviceType = {
              deviceCode: response.data.device_code,
              deviceName: response.data.device_name,
            }

            // ✅ Set form dari API response
            this.form = {
              ...this.form,
              imei: this.taskData.imei,
              phoneNo: this.taskData.phone_no || response.data.phone_number || null,
              companyId: response.data.company_id || this.taskData.company_id || null,
              deviceTypes: deviceType,
              vehicleId: response.data.vehicle_id || null,
            }

            // ★ Set flag TRUE - IMEI sudah terdaftar
            this.isIMEIRegistered = true
            return true
          } else {
            // ❌ IMEI tidak ada di DB
            console.warn('Device not found in API (no data)')
            this.isIMEIRegistered = false // ★ Set flag FALSE
            return false
          }
        } catch (apiError) {
          // ❌ API error atau 404 - IMEI tidak ada
          console.error('API error checking IMEI:', apiError)

          // ★ Set flag FALSE - IMEI belum di-register
          this.isIMEIRegistered = false

          if (apiError.response?.status === 404) {
            console.warn('IMEI not found in database (404)')
          }
          return false
        }
      } catch (error) {
        console.error('❌ checkDeviceAlreadyRegistered error:', error)
        this.isIMEIRegistered = false // ★ Set flag FALSE
        return false
      }
    },

    nextSetup(item) {
      localStorage.setItem('device', JSON.stringify(item))
      this.$router.push({
        name: 'Setting Device',
      })
    },

    async getDeviceTypesWithParams(params = {}) {
      try {
        this.isLoading = true
        params = {
          limit: 1,
          page: 1,
        }
        let response = await deviceTypeApi.get(params)
        if (response.status && response.metadata.total_count > 1) {
          params.limit = response.metadata.total_count
          response = await deviceTypeApi.get(params)
        }
        this.deviceTypes = response.data.paginated_result
        this.isLoading = false
      } catch (error) {
        console.log('Error loading device types:', error)
        this.isLoading = false
      }
    },

    async initialize() {
      this.hasShownManualSetToToast = false
      // ✅ PENTING: Pastikan deviceTypes sudah loaded sebelum proceed
      if (!this.deviceTypes || this.deviceTypes.length === 0) {
        await this.getDeviceTypesWithParams()
      }

      // 🔥 Reset flags
      this.preview = {}
      this.set.imei = this.imei
      this.isIMEIRegistered = false // ★ Reset flag setiap kali modal dibuka
      this.isLoading = true

      try {
        const [sopResponse, tasksChecklistResponse] = await Promise.all([
          checklistApi.getLists({
            limit: 1,
            page: 1,
            filters: [`wo_type eq 'I'`, `flag eq 'CoordinatorSop'`],
          }),
          taskChecklistApi.get({
            limit: 1,
            page: 1,
            filters: [`tasks_id eq ${this.taskId}`],
          }),
        ])

        if (sopResponse.status) {
          this.formData = sopResponse.data?.paginated_result?.[0]
        }

        // Map dari tasks_checklist_detail
        const taskDetailMap = {}
        const existing = tasksChecklistResponse?.data?.paginated_result || []
        existing.forEach((tc) => {
          if (tc.tasks_checklist_detail && Array.isArray(tc.tasks_checklist_detail)) {
            tc.tasks_checklist_detail.forEach((detail) => {
              taskDetailMap[detail.checklist_detail_id] = {
                check: detail.check || false,
                photo_url: detail.photo || null,
              }
            })
          }
        })

        let details = []
        if (this.formData?.checklist_detail) {
          details = this.formData.checklist_detail.map((d) => {
            const taskData = taskDetailMap[d.checklist_detail_id] || {}
            return {
              ...d,
              check: taskData.check || false,
              photo_url: taskData.photo_url || null,
            }
          })
        }
        this.localDetails = details

        // --- Perbaikan: Resolve foto hanya jika ada photo_url dari task ---
        const uploadDetails = this.localDetails.filter(
          (d) => d.checklist_detail_type.includes('U') && d.photo_url
        )
        await Promise.all(
          uploadDetails.map(async (detail) => {
            try {
              let fullUrl = detail.photo_url
              // Jika bukan URL lengkap, coba resolve dengan fileApi
              if (!fullUrl.startsWith('http')) {
                const response = await fileApi.getByUrl(fullUrl)
                if (response?.data?.url) {
                  fullUrl = response.data.url
                } else {
                  return // tidak ada URL valid, skip
                }
              }
              this.$set(this.preview, detail.checklist_detail_id, fullUrl)
            } catch (e) {
              console.warn('Gagal resolve foto untuk detail', detail.checklist_detail_id, e)
            }
          })
        )

        // 4. Cek status device (RCD dan SD)
        // ✅ Cek status RCD dan SD
        this.isEditRegister = await this.checkDeviceAlreadyRegistered()
        // ★ isIMEIRegistered sudah di-set di checkDeviceAlreadyRegistered

        this.isEditSetTo = await this.checkDeviceAlreadySetToCustomer()

        this.$nextTick(() => {
          if (this.isManualSetTo && !this.hasShownManualSetToToast) {
            this.hasShownManualSetToToast = true

            this.$mcToast.error(
              'Device sudah terpasang pada kendaraan lain. Silakan lakukan Unregister terlebih dahulu sebelum melakukan SOP.'
            )
          }
        })

        this.isLoading = false
      } catch (error) {
        console.error('Error in initialize:', error)
        this.isLoading = false
      }
    },

    buildFileName(item, file) {
      const safeName = this.sanitizeFileName(item.description || 'file')
      const ext = file?.name?.split('.').pop() || 'png'

      return `${safeName}-${item.checklist_detail_id}.${ext}`
    },

    async loadExistingTaskChecklist() {
      const response = await taskChecklistApi.getByTasksId(this.taskId)
      response.forEach((saved) => {
        const found = this.localDetails.find(
          (x) => x.checklist_detail_id === saved.checklist_detail_id
        )
        if (!found) return
        found.photo_url = saved.photo
        found.check = saved.check
      })
    },

    onCancel() {
      this.$emit('input', false)
    },

    onHidden() {
      this.$emit('input', false)
    },

    async saveWithStatus(status) {
      // Validasi khusus untuk status 'C' (Created)
      if (status === 'C') {
        const incomplete = this.filteredDetails.filter((d) => !d.check)
        if (incomplete.length > 0) {
          const names = incomplete.map((d) => d.description).join(', ')
          this.$mcToast.error(`SOP berikut belum selesai: ${names}`)
          return
        }
      }

      this.isLoading = true
      this.isSaving = true

      try {
        let payload = { tasks_status: status }

        // Untuk status 'C', kirim data lengkap yang dibutuhkan backend untuk validasi
        if (status === 'C') {
          payload = {
            ...payload,
            company_id: this.set.companies?.company_id,
            vehicle_type_id: this.set.selectedVehicleType?.vehicle_type_id,
            vehicle_id: this.set.selectedVehicle?.vehicle_id,
            phone_no: this.set.phoneNo,
            fuel_type: this.set.selectedVehicleType?.fuel_type_name,
            // tambahkan field lain jika diperlukan (misal imei, license_plate, dll)
            imei: this.set.imei,
            license_plate: this.set.selectedVehicle?.license_plate,
          }
        }
        // Untuk status 'D' (Draft), payload hanya berisi tasks_status,
        // sehingga backend hanya akan mengupdate status tanpa efek samping.

        const response = await tasksApi.update(this.taskId, payload)
        console.log(response)
        if (response?.data?.status === 200) {
          this.$mcToast.success(`SOP ${this.formData.name} berhasil disimpan dengan status ${status}`)
        }

        this.$emit('saved')
        this.$emit('input', false)
      } catch (error) {
        this.$mcToast.error('Gagal menyimpan perubahan')
      } finally {
        this.isLoading = false
        this.isSaving = false
      }
    },

    // dipanggil saat klik area upload
    triggerFile(id) {
      // safety check
      const el = document.getElementById('upl-' + id)
      if (!el) {
        console.warn('triggerFile: element not found', id)
        return
      }
      el.click()
    },

    renameFile(file, newName) {
      return new File([file], newName, { type: file.type })
    },

    sanitizeFileName(text) {
      if (!text) return 'file'

      return text
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '')
    },

    async onFileSelected(event, item) {
      const input = event.target
      const file = input?.files?.[0]
      if (!file) return

      item.check = false

      // preview tetap pakai file asli (tidak masalah)
      const localUrl = URL.createObjectURL(file)
      this.$set(this.preview, item.checklist_detail_id, localUrl)

      input.disabled = true

      try {
        const safeName = this.sanitizeFileName(item.description || 'file')
        const ext = file.name.split('.').pop()
        const newFileName = `${safeName}-${item.checklist_detail_id}.${ext}`

        // 🔥 RENAME FILE DI SINI
        const renamedFile = this.renameFile(file, newFileName)

        const formData = new FormData()
        formData.append('file', renamedFile) // 👈 nama sudah berubah
        formData.append('url', 'checklist/')

        const response = await fileApi.upload(formData)
        item.photo_url = response.data?.path || ''
        item.check = true
        await this.updateChecklistItem(item)

        // ✅ TAMBAH: Toast success setelah upload berhasil
        this.$mcToast.success(`Foto "${item.description}" berhasil diunggah`)
      } catch (err) {
        console.error('Upload gagal:', err)
        item.check = false

        // ✅ PERBAIKI: Gunakan mcToast untuk error juga (consistent)
        this.$mcToast.error('Gagal mengunggah foto. Silakan coba lagi.')

        this.$delete(this.preview, item.checklist_detail_id)
      } finally {
        input.disabled = false
        input.value = ''
      }
    },

    // dipanggil saat file di-drop ke area
    async onDrop(event, item) {
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      if (!file) return
      // reuse onFileSelected logic by creating a fake event-like object
      const fakeEvent = { target: { files: [file], disabled: false, value: '' } }
      await this.onFileSelected(fakeEvent, item)
      this.isDragging = false
    },

    async onCheckAndUpdateWorkOrderErp() {
      this.isLoadingCheckUpdate = true
      try {
        // Ambil data yang sudah ada dari state `set` (diisi di initialize)
        const payload = {
          vehicle: {
            vehicle_id: this.set.selectedVehicle?.vehicle_id,
            company_id: this.set.companies?.company_id,
            license_plate: this.set.selectedVehicle?.license_plate,
            vehicle_type_id: this.set.selectedVehicleType?.vehicle_type_id,
            year: parseInt(this.set.vehicleYear),
            odometer: parseInt(this.set.odometer) * 1000,
            hull_no: this.set.hullNo || '',
            phone_no: this.set.phoneNo,
            imei: this.taskData.imei,
          },
          update_erp: true, // <-- add this
          vendor_type: this.set.companies?.name, // nama company
          checklist_updates: [
            {
              checklist_detail_id: this.cucwoDetail.checklist_detail_id,
              check: true,
            },
          ],
        }

        const response = await taskChecklistApi.migrateWorkOrder(this.taskId, payload)
        if (response.status) {
          this.$mcToast.success('Check & Update Komponen Work Order Berhasil')
          // Tandai checklist CUCWO selesai di local state
          const cucwoDetail = this.localDetails.find((d) =>
            d.checklist_detail_type.includes('CUCWO')
          )
          if (cucwoDetail) cucwoDetail.check = true
          this.$emit('data-updated')   // ← hanya refresh, tidak tutup modal
        }
      } catch (error) {
        this.$mcToast.error('Gagal update Work Order')
      } finally {
        this.isLoadingCheckUpdate = false
      }
    },

    async onUpdateVehicle() {
      this.setDeviceToCustomerLoading = true
      this.$v.set.$touch()
      if (this.$v.set.$error) {
        this.setDeviceToCustomerLoading = false
        return
      }

      try {
        const payload = {
          vehicle: {
            vehicle_id: this.set.selectedVehicle.vehicle_id,
            company_id: this.set.companies.company_id,
            license_plate: this.set.selectedVehicle.license_plate,
            vehicle_type_id: this.set.selectedVehicleType.vehicle_type_id,
            year: parseInt(this.set.vehicleYear) || 0,
            odometer: parseInt(this.set.odometer) * 1000,
            hull_no: this.set.hullNo || '',
            phone_no: this.set.phoneNo,
            imei: this.set.imei,
          },
          checklist_updates: [
            {
              checklist_detail_id: this.sdDetail.checklist_detail_id,
              check: true,
            },
          ],
        }
        const response = await taskChecklistApi.migrateWorkOrder(this.taskId, payload)
        if (response.status) {
          this.$mcToast.success('Data kendaraan berhasil diperbarui')
          const sdDetail = this.localDetails.find((d) => d.checklist_detail_type.includes('SD'))
          if (sdDetail) sdDetail.check = true
          this.$emit('data-updated')   // ← hanya refresh, tidak tutup modal
        }
      } catch (error) {
        this.$mcToast.error('Gagal update kendaraan')
      } finally {
        this.setDeviceToCustomerLoading = false
      }
    },

    async registerToCompany() {
      this.setDeviceToCustomerLoading = true
      this.$v.set.$touch()
      if (this.$v.set.$error) {
        this.setDeviceToCustomerLoading = false
        return
      }

      try {
        const payload = {
          vehicle: {
            vehicle_id: this.set.selectedVehicle.vehicle_id,
            company_id: this.set.companies.company_id,
            license_plate: this.set.selectedVehicle.license_plate,
            vehicle_type_id: this.set.selectedVehicleType.vehicle_type_id,
            year: parseInt(this.set.vehicleYear),
            odometer: parseInt(this.set.odometer) * 1000,
            hull_no: this.set.hullNo,
            phone_no: this.set.phoneNo,
            imei: this.set.imei, // IMEI dari task
          },
          // tidak kirim device
          checklist_updates: [
            {
              checklist_detail_id: this.sdDetail.checklist_detail_id,
              check: true,
            },
          ],
        }
        const response = await taskChecklistApi.migrateWorkOrder(this.taskId, payload)
        if (response.status) {
          this.$mcToast.success('Device berhasil dipasangkan ke kendaraan')
          this.isEditSetTo = true
          const sdDetail = this.localDetails.find((d) => d.checklist_detail_type.includes('SD'))
          if (sdDetail) sdDetail.check = true
          this.$emit('data-updated')   // ← hanya refresh, tidak tutup modal
        }
      } catch (error) {
        this.$mcToast.error('Gagal assign device')
      } finally {
        this.setDeviceToCustomerLoading = false
      }
    },

    async processSubmit() {
      this.isLoading = true
      try {
        const payload = {
          device: {
            imei: this.form.imei,
            device_code: parseInt(this.form.deviceTypes.deviceCode),
            phone_number: this.form.phoneNo,
            vendor_type: this.set.companies.name, // nama company
          },
          checklist_updates: [
            {
              checklist_detail_id: this.rcdDetail.checklist_detail_id,
              check: true,
            },
          ],
        }
        const response = await taskChecklistApi.migrateWorkOrder(this.taskId, payload)
        if (response.status) {
          this.$mcToast.success('Device berhasil diregistrasi')
          const rcdDetail = this.localDetails.find((d) => d.checklist_detail_type.includes('RCD'))
          if (rcdDetail) rcdDetail.check = true
          this.$emit('data-updated')   // ← hanya refresh, tidak tutup modal
        }
      } catch (error) {
        this.$mcToast.error('Gagal registrasi device', error)
      } finally {
        this.isLoading = false
      }
    },

    async updateDevice() {
      this.isLoading = true
      try {
        const payload = {
          device: {
            imei: this.taskData.imei,
            device_code: this.form.deviceTypes.deviceCode,
            phone_number: this.form.phoneNo,
            vendor_type: this.set.companies.name, // nama company
          },
          // tidak kirim vehicle
          checklist_updates: [
            {
              checklist_detail_id: this.rcdDetail.checklist_detail_id,
              check: true,
            },
          ],
        }
        const response = await taskChecklistApi.migrateWorkOrder(this.taskId, payload)
        if (response.status) {
          this.$mcToast.success('Device berhasil diperbarui')
          // Update local state
          const rcdDetail = this.localDetails.find((d) => d.checklist_detail_type.includes('RCD'))
          if (rcdDetail) rcdDetail.check = true
          this.$emit('data-updated')   // ← hanya refresh, tidak tutup modal
        }
      } catch (error) {
        this.$mcToast.error('Gagal update device', error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.checklist-accordion {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Accordion Card */
.card {
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.card.disabled-section {
  border-color: #f0f0f0;
  background-color: #fafafa;
}

/* Accordion Header Button */
.accordion-header-btn {
  padding: 1rem !important;
  border: none;
  border-radius: 0 !important;
  text-decoration: none !important;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
}

.accordion-header-btn:not(:disabled):hover {
  background-color: #e9ecef;
  color: #0d6efd;
}

.accordion-header-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: #f5f5f5;
}

.accordion-header-btn.completed {
  background-color: #d4edda;
  border-left: 4px solid #28a745;
  color: #155724;
}

.accordion-header-btn.completed:not(:disabled):hover {
  background-color: #c3e6cb;
}

/* Accordion Content */
.accordion-content {
  border-top: 1px solid #dee2e6;
}

.card-body {
  padding: 1.5rem;
}

/* Badge styling */
.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
  margin-left: 0.5rem;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

/* Opacity untuk disabled */
.opacity-50 {
  opacity: 0.5;
}

.checklist-header {
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.checklist-content {
  padding-top: 4px;
}

.upload-wrapper {
  position: relative;
  width: 100%;
}

.hidden-file {
  display: none !important;
}

.upload-box {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-box:hover {
  border-color: #0d6efd;
}

.upload-box img.uploaded-image {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
}

.upload-box.dragging {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

/* Checkbox lebih besar */
.checkbox-lg .custom-control-label::before,
.checkbox-lg .custom-control-label::after {
  width: 24px;
  height: 24px;
}

.animated-check .custom-control-input:checked ~ .custom-control-label::before {
  transition: all 0.2s ease-in-out;
}

.animated-check .custom-control-input:checked ~ .custom-control-label::after {
  transform: scale(1.1);
  transition: transform 0.2s ease-in-out;
}
</style>
